import {
    cn,
    useDisclosure,
    type UseDisclosureReturn
} from "@gettactic/helpers";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { DropdownMenuContentVariants } from "@cmp-src/primitives/dropdown-menu";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import {
    SelectItem,
    SelectItemContext,
    useSelectItemContext
} from "@cmp-src/primitives/Select/select-items/SelectItem";
import { CheckIcon } from "lucide-react";
import { collectChildrenByDisplayName } from "@gettactic/helpers/src/collectChildrenByDisplayName";
import { SearchInput } from "@cmp-src/controls/SearchInput/SearchInput";
import { cva, type VariantProps } from "class-variance-authority";

type PrimitiveSelectRootProps = React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Root
> & {
    mobileDisclosure?: UseDisclosureReturn;
};

export interface SelectState {
    isMobile: boolean;
    mobileDisclosure: UseDisclosureReturn;
    value?: PrimitiveSelectRootProps["value"];
    onValueChange: PrimitiveSelectRootProps["onValueChange"];
    valueItems: Record<string, { element: React.ReactNode; text: string[] }>;
    isDisabled?: boolean;
    searchValue: string;
    setSearchValue: (value: string) => void;
    isOpen?: boolean;
    setIsOpen?: (value: boolean) => void;
}

export const SelectContext = React.createContext<SelectState | undefined>(
    undefined
);

export const useSelectContext = () => {
    const context = React.useContext(SelectContext);
    if (context === undefined) {
        return {} as SelectState;
    }
    return context;
};

const Select: React.FC<PrimitiveSelectRootProps> = ({ ...props }) => {
    const { isBreakpoint } = useBreakpoint();
    const isMd = isBreakpoint("md");
    const _mobileDisclosure = useDisclosure();
    const mobileDisclosure = props.mobileDisclosure || _mobileDisclosure;
    const [value, setValue] = React.useState<PrimitiveSelectRootProps["value"]>();
    const _value = props.value ?? value;
    const isMobile = !isMd;

    const valueItems = React.useMemo(() => {
        if (!SelectItem.displayName) return {};

        return collectChildrenByDisplayName(
            props.children,
            SelectItem.displayName,
            {
                valueProp: "value"
            }
        );
    }, [props.children]);

    const desktopOnValueChange = isMobile ? undefined : props.onValueChange;
    const [searchValue, setSearchValue] = React.useState("");
    return (
        <SelectContext.Provider
            value={{
                isMobile,
                mobileDisclosure,
                value: _value,
                onValueChange: (value) => {
                    props.onValueChange?.(value);
                    setValue(value);
                    mobileDisclosure.close();
                    setSearchValue("");
                },
                valueItems,
                isDisabled: props.disabled,
                searchValue,
                setSearchValue,
                isOpen: undefined,
                setIsOpen: undefined
            }}
        >
            <SelectPrimitive.Root {...props} onValueChange={desktopOnValueChange} />
        </SelectContext.Provider>
    );
};

export const MobileSelectItemIndicator: React.FC<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.SelectItemIndicator>
> = (props) => {
    const { isSelected } = useSelectItemContext();

    if (!isSelected) return null;

    return (
        <div {...props}>
            <CheckIcon className={"size-4"} />
        </div>
    );
};

const SelectGroup = SelectPrimitive.Group;
const SelectItemIndicator: React.FC<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.SelectItemIndicator>
> = (props) => {
    const { isMobile } = useSelectContext();

    if (isMobile) {
        return <MobileSelectItemIndicator {...props} />;
    }

    return <SelectPrimitive.SelectItemIndicator {...props} />;
};

const SelectItemText: React.FC<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.SelectItemText>
> = (props) => {
    const { isMobile } = useSelectContext();
    const { isValueItem } = useSelectItemContext();

    if (isMobile) {
        return (
            <div
                className={cn(
                    "flex-1 truncate text-left md:pb-2",
                    !isValueItem && "pr-6",
                    props.className
                )}
            >
                {props.children}
            </div>
        );
    }

    return (
        <SelectPrimitive.SelectItemText
            {...props}
            className={cn(
                "flex-1 text-left",
                !isValueItem && "px-6",
                props.className
            )}
        />
    );
};

const SelectValue = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Value>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>((props, ref) => {
    const { isMobile, valueItems, value } = useSelectContext();

    if (isMobile) {
        if (value !== undefined && value !== null && valueItems[value]) {
            return (
                <SelectItemContext.Provider
                    value={{
                        isSelected: false,
                        value: value,
                        isValueItem: true
                    }}
                >
                    {valueItems[value].element}
                </SelectItemContext.Provider>
            );
        }
        return (
            <div
                className={
                    "flex h-8 w-full items-center whitespace-nowrap min-w-0 truncate"
                }
            >
                {props.placeholder || ""}
            </div>
        );
    }

    return (
        <span className={cn("min-w-0 flex-1 truncate", props.className)}>
      <span style={{ pointerEvents: "none" }} className={"font-inter"}>
        {props.children}
      </span>
    </span>
    );
});

const selectTriggerVariants = cva("cursor-pointer", {
    variants: {
        variant: {
            default:
                "border border-neutral-100 flex h-10 min-h-[42px] w-full min-w-0 items-center justify-start gap-1 rounded-brand bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            gray: "border-0 flex h-10 min-h-[42px] w-full min-w-0 items-center justify-start gap-1 rounded-md bg-[#f7f9fb] px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            ghost:
                "border-0 h-auto min-h-0 w-auto min-w-0 rounded-none bg-transparent px-0 py-0 text-base ring-offset-0 hover:bg-transparent hover:border-0 active:bg-transparent active:border-0 active:ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 disabled:bg-transparent disabled:border-0 disabled:opacity-100"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});

export interface SelectTriggerProps
    extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
        VariantProps<typeof selectTriggerVariants> {
    showArrow?: boolean;
}

const SelectTrigger = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Trigger>,
    SelectTriggerProps
>(({ className, children, showArrow, variant, ...props }, ref) => {
    const { isMobile, mobileDisclosure, isDisabled, setIsOpen } =
        useSelectContext();

    const isGhost = variant === "ghost";

    if (isMobile) {
        return (
            <button
                type="button"
                disabled={isDisabled}
                onClick={() => (setIsOpen ? setIsOpen(true) : mobileDisclosure.open())}
                ref={ref}
                className={cn(
                    selectTriggerVariants({ variant }),
                    variant !== "ghost" &&
                    "items-center justify-start overflow-hidden pt-1",
                    className
                )}
                {...props}
            >
                {children}
                {showArrow !== false && (
                    <SelectPrimitive.Icon asChild>
                        <ChevronDown className="mt-0.5 h-4 w-4 opacity-50" />
                    </SelectPrimitive.Icon>
                )}
            </button>
        );
    }

    return (
        <SelectPrimitive.Trigger
            ref={ref}
            className={cn(
                variant !== "ghost" && "select_trigger",
                selectTriggerVariants({ variant }),
                className
            )}
            {...props}
        >
            {children}
            {showArrow !== false && (
                <SelectPrimitive.Icon asChild>
                    <ChevronDown className="h-4 w-4" />
                </SelectPrimitive.Icon>
            )}
        </SelectPrimitive.Trigger>
    );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectClearTrigger = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
    const { isMobile, mobileDisclosure, isDisabled } = useSelectContext();

    const _classNAme = cn(
        "mr-1 flex h-10 min-h-[42px] w-full cursor-pointer items-center justify-between bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        className
    );

    if (isMobile) {
        return (
            <button
                type={"button"}
                disabled={isDisabled}
                onClick={() => mobileDisclosure.open()}
                ref={ref}
                className={_classNAme}
                {...props}
            >
                {children}
                <SelectPrimitive.Icon asChild>
                    <ChevronDown className="h-4 w-4" />
                </SelectPrimitive.Icon>
            </button>
        );
    }

    return (
        <SelectPrimitive.Trigger ref={ref} className={_classNAme} {...props}>
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDown className="h-4 w-4" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
});
SelectClearTrigger.displayName = "SelectClearTrigger";

const SelectContentVariants = {
    default: "border border-neutral-200 shadow-md",
    dropdown: DropdownMenuContentVariants.dropdown
};

export const SelectContentPositionClassNames = {
    borderDividerBottom: "border-neutral-100 border-b",
    borderDividerTop: "border-neutral-100 border-t"
};
type SelectContentProps = React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Content
> & {
    TopElement?: React.ReactNode;
    BottomElement?: React.ReactNode;
    scrollViewportClassname?: string;
    variant?: keyof typeof SelectContentVariants;
};
const SelectContent = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Content>,
    SelectContentProps
>(
    (
        {
            className,
            children,
            position = "popper",
            TopElement,
            BottomElement,
            scrollViewportClassname,
            variant = "default",
            ...props
        },
        ref
    ) => {
        const {
            isMobile,
            mobileDisclosure,
            setSearchValue,
            searchValue,
            isOpen,
            setIsOpen
        } = useSelectContext();

        if (isMobile) {
            return (
                <>
                    <MobileDrawer
                        isOpen={isOpen || mobileDisclosure.isOpen}
                        onOpenChange={(isOpen) => {
                            setIsOpen?.(isOpen);
                            mobileDisclosure.onOpenChange(isOpen);
                        }}
                        topContent={
                            TopElement ? (
                                <div
                                    className={cn(
                                        SelectContentPositionClassNames.borderDividerBottom
                                    )}
                                >
                                    {TopElement}
                                </div>
                            ) : (
                                <div className={"mb-2 w-full border-neutral-200 border-b px-0"}>
                                    <SearchInput
                                        fullWidth
                                        className={"w-full border-0"}
                                        query={searchValue || ""}
                                        setQuery={(query: string) => setSearchValue(query || "")}
                                    />
                                </div>
                            )
                        }
                        scrollableContent
                    >
                        <div className={"min-h-[300px]"} />
                        <div
                            className={cn(
                                "absolute inset-0 flex flex-col overflow-auto",
                                !!BottomElement && "mb-14"
                            )}
                        >
                            <div className={"flex-1"}>{children}</div>
                        </div>
                        {!!BottomElement && (
                            <div
                                className={cn(
                                    SelectContentPositionClassNames.borderDividerTop,
                                    "py-1"
                                )}
                            >
                                {BottomElement}
                            </div>
                        )}
                    </MobileDrawer>
                </>
            );
        }

        return (
            <SelectPrimitive.Portal>
                <SelectPrimitive.Content
                    ref={ref}
                    className={cn(
                        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-dropdown min-w-[15.3rem] md:max-w-[28rem] overflow-hidden rounded-border bg-popover p-0 text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in",
                        SelectContentVariants[variant],
                        position === "popper" &&
                        "data-[side=left]:-translate-x-1 data-[side=top]:-translate-y-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1",
                        className
                    )}
                    position={position}
                    {...props}
                >
                    {!!TopElement && (
                        <div
                            className={cn(
                                SelectContentPositionClassNames.borderDividerBottom
                            )}
                        >
                            {TopElement}
                        </div>
                    )}
                    <ScrollArea.Root type="auto">
                        <SelectPrimitive.Viewport
                            asChild
                            className={cn(
                                position === "popper" &&
                                "mr-3 w-full min-w-[var(--radix-select-trigger-width)]"
                            )}
                        >
                            <ScrollArea.Viewport
                                className={cn(
                                    "max-h-60 min-h-[var(--radix-select-trigger-height)] py-2",
                                    scrollViewportClassname
                                )}
                                // to prevent Updating a style property during rerender (overflowY)
                                style={{ overflowY: undefined }}
                            >
                                {children}
                            </ScrollArea.Viewport>
                        </SelectPrimitive.Viewport>
                        <ScrollArea.Scrollbar
                            orientation="vertical"
                            className="flex w-2 p-[2px] hover:bg-neutral-50"
                        >
                            <ScrollArea.Thumb className="w-full cursor-pointer rounded-full bg-neutral-200" />
                        </ScrollArea.Scrollbar>
                    </ScrollArea.Root>
                    {!!BottomElement && (
                        <div
                            className={cn(
                                SelectContentPositionClassNames.borderDividerTop,
                                "py-1"
                            )}
                        >
                            {BottomElement}
                        </div>
                    )}
                </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
        );
    }
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cn("py-1.5 pr-2 pl-8 font-semibold text-sm", className)}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectSeparator = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cn("my-1 h-px bg-muted", className)}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectGroup,
    SelectValue,
    SelectTrigger,
    SelectContent,
    SelectLabel,
    SelectSeparator,
    SelectClearTrigger,
    SelectItemIndicator,
    SelectItemText
};
